import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useFormik, FormikValues } from "formik";
import { HexColorPicker } from "react-colorful";
import {
  useCreateDarkThemeColorMutation,
  useCreateLightThemeColorMutation,
  useGetAllDarkThemeColorQuery,
  useGetAllLightThemeColorQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { IAuth } from "../../../Api/Interface/api.interface";
import { useThemeContext } from "../../../Drawer/Menu/ThemeContext";

interface ILightTheme {
  id: string;
  borderRadius: string;
  IconButtonBorderRadius: string;
  cardBorderRadius: string;
  primary: string;
  secondary: string;
  textPrimary: string;
  textSecondary: string;
  heading: string;
  divider: string;
  topBar: string;
  topBarHeading: string;
  drawerHeader: string;
  drawer: string;
  drawerHeadingText: string;
  drawerMenuText: string;
  background: string;
  secondBackground: string;
  cardBackground: string;
  editButtonColor: string;
  editIconColor: string;
  viewButtonColor: string;
  viewIconColor: string;
  deleteButtonColor: string;
  deleteIconColor: string;
}

interface IDarkTheme {
  id: string;
  borderRadius: string;
  IconButtonBorderRadius: string;
  cardBorderRadius: string;
  primary: string;
  secondary: string;
  textPrimary: string;
  textSecondary: string;
  heading: string;
  divider: string;
  topBar: string;
  topBarHeading: string;
  drawerHeader: string;
  drawer: string;
  drawerHeadingText: string;
  drawerMenuText: string;
  background: string;
  secondBackground: string;
  cardBackground: string;
  editButtonColor: string;
  editIconColor: string;
  viewButtonColor: string;
  viewIconColor: string;
  deleteButtonColor: string;
  deleteIconColor: string;
}

enum Section {
  Dark = "Dark",
  Light = "Light",
}

const Index: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState<Section>(Section.Light);
  const handleContainerChange = (section: Section) => {
    setActiveSection(section);
  };
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const { mode, toggleTheme } = useThemeContext();

  const { data, isLoading } = useGetAllLightThemeColorQuery({
    searchText: "",
    id: "1",
  });
  const { data: darkData, isLoading: darkIsLoading } =
    useGetAllDarkThemeColorQuery({
      searchText: "",
      id: "1",
    });

  const [settingData] = useCreateLightThemeColorMutation();
  const [darkthemeData] = useCreateDarkThemeColorMutation();

  const [initialData, setInitialData] = useState<ILightTheme | null>(null);
  const [selectedField, setSelectedField] = useState<string>("");
  const [tempColor, setTempColor] = useState<string>("");
  const [initialDarkData, setInitialDarkData] = useState<IDarkTheme | null>(
    null
  );
  const [selectedDarkField, setSelectedDarkField] = useState<string>("");
  const [tempColorDark, setTempColorDark] = useState<string>("");

  useEffect(() => {
    if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
      const themeColor = data.data[0];
      setInitialData({
        id: themeColor.id.toString(),
        borderRadius: themeColor.borderRadius || "16px",
        IconButtonBorderRadius: themeColor.IconButtonBorderRadius || "16px",
        cardBorderRadius: themeColor.cardBorderRadius || "none",
        primary: themeColor.primary || "#9d939f",
        secondary: themeColor.secondary || "#837d86",
        textPrimary: themeColor.textPrimary || "#7e7e7e",
        textSecondary: themeColor.textSecondary || "#776c7b",
        heading: themeColor.heading || "#735784",
        divider: themeColor.divider || "#cfc9d1",
        topBar: themeColor.topBar || "#fff",
        topBarHeading: themeColor.topBarHeading || "#735784",
        drawerHeader: themeColor.drawerHeader || "#fff",
        drawer: themeColor.drawer || "#2e1c39",
        drawerHeadingText: themeColor.drawerHeadingText || "#f3ecf5",
        drawerMenuText: themeColor.drawerMenuText || "#f3ecf5",
        background: themeColor.background || "#f5ecfa",
        secondBackground: themeColor.secondBackground || "#f3ecf5",
        cardBackground: themeColor.cardBackground || "#ffffff",
        editButtonColor: themeColor.editButtonColor || "#939482",
        editIconColor: themeColor.editIconColor || "#ffffff",
        viewButtonColor: themeColor.viewButtonColor || "#695940",
        viewIconColor: themeColor.viewIconColor || "#ffffff",
        deleteButtonColor: themeColor.deleteButtonColor || "#6b5654",
        deleteIconColor: themeColor.deleteIconColor || "#ffffff",
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      darkData?.data &&
      Array.isArray(darkData.data) &&
      darkData.data.length > 0
    ) {
      const themeColor = darkData.data[0];
      setInitialDarkData({
        id: themeColor.id.toString(),
        borderRadius: themeColor.borderRadius || "16px",
        IconButtonBorderRadius: themeColor.IconButtonBorderRadius || "16px",
        cardBorderRadius: themeColor.cardBorderRadius || "none",
        primary: themeColor.primary || "#9d939f",
        secondary: themeColor.secondary || "#8c9cb7",
        textPrimary: themeColor.textPrimary || "#cec7c7",
        textSecondary: themeColor.textSecondary || "#d1d1d1",
        heading: themeColor.heading || "#aab8c5",
        divider: themeColor.divider || "#6b6b6b",
        topBar: themeColor.topBar || "#22282e",
        topBarHeading: themeColor.topBarHeading || "#aab8c5",
        drawerHeader: themeColor.drawerHeader || "#22282e",
        drawer: themeColor.drawer || "#3e3946",
        drawerHeadingText: themeColor.drawerHeadingText || "#4d4b4e",
        drawerMenuText: themeColor.drawerMenuText || "#4d4b4e",
        background: themeColor.background || "#282828",
        secondBackground: themeColor.secondBackground || "#4d4b4e",
        cardBackground: themeColor.cardBackground || "#2f3944",
        editButtonColor: themeColor.editButtonColor || "#c5bccf",
        editIconColor: themeColor.editIconColor || "#ffffff",
        viewButtonColor: themeColor.viewButtonColor || "#858d74",
        viewIconColor: themeColor.viewIconColor || "#ffffff",
        deleteButtonColor: themeColor.deleteButtonColor || "#765b58",
        deleteIconColor: themeColor.deleteIconColor || "#ffffff",
      });
    }
  }, [darkData]);

  const formik = useFormik<ILightTheme>({
    initialValues: {
      id: initialData?.id || "",
      borderRadius: initialData?.borderRadius || "16px",
      IconButtonBorderRadius: initialData?.IconButtonBorderRadius || "16px",
      cardBorderRadius: initialData?.cardBorderRadius || "none",
      primary: initialData?.primary || "#9d939f",
      secondary: initialData?.secondary || "#837d86",
      textPrimary: initialData?.textPrimary || "#7e7e7e",
      textSecondary: initialData?.textSecondary || "#776c7b",
      heading: initialData?.heading || "#735784",
      divider: initialData?.divider || "#cfc9d1",
      topBar: initialData?.topBar || "#34495E",
      topBarHeading: initialData?.topBarHeading || "#735784",
      drawerHeader: initialData?.drawerHeader || "#fff",
      drawer: initialData?.drawer || "#2e1c39",
      drawerHeadingText: initialData?.drawerHeadingText || "#f3ecf5",
      drawerMenuText: initialData?.drawerMenuText || "#f3ecf5",
      background: initialData?.background || "#f5ecfa",
      secondBackground: initialData?.secondBackground || "#f3ecf5",
      cardBackground: initialData?.cardBackground || "#ffffff",
      editButtonColor: initialData?.editButtonColor || "#939482",
      editIconColor: initialData?.editIconColor || "#ffffff",
      viewButtonColor: initialData?.viewButtonColor || "#695940",
      viewIconColor: initialData?.viewIconColor || "#ffffff",
      deleteButtonColor: initialData?.deleteButtonColor || "#6b5654",
      deleteIconColor: initialData?.deleteIconColor || "#ffffff",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = initialData
          ? { ...values, id: initialData.id }
          : { ...values };

        const response = await settingData(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as IAuth;
          if (tokenData) {
            localStorage.setItem("lightThemeColor", JSON.stringify(tokenData));
            window.location.reload();
          }
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikDark = useFormik<IDarkTheme>({
    initialValues: {
      id: initialDarkData?.id || "4px",
      borderRadius: initialDarkData?.borderRadius || "16px",
      IconButtonBorderRadius: initialDarkData?.IconButtonBorderRadius || "16px",
      cardBorderRadius: initialData?.cardBorderRadius || "none",
      primary: initialDarkData?.primary || "#9d939f",
      secondary: initialDarkData?.secondary || "#8c9cb7",
      textPrimary: initialDarkData?.textPrimary || "#cec7c7",
      textSecondary: initialDarkData?.textSecondary || "#d1d1d1",
      heading: initialDarkData?.heading || "#aab8c5",
      divider: initialDarkData?.divider || "#6b6b6b",
      topBar: initialDarkData?.topBar || "#22282e",
      topBarHeading: initialDarkData?.topBarHeading || "#aab8c5",
      drawerHeader: initialDarkData?.drawerHeader || "#22282e",
      drawer: initialDarkData?.drawer || "#3e3946",
      drawerHeadingText: initialDarkData?.drawerHeadingText || "#4d4b4e",
      drawerMenuText: initialDarkData?.drawerMenuText || "#4d4b4e",
      background: initialDarkData?.background || "#282828",
      secondBackground: initialDarkData?.secondBackground || "#4d4b4e",
      cardBackground: initialDarkData?.cardBackground || "#2f3944",
      editButtonColor: initialDarkData?.editButtonColor || "#c5bccf",
      editIconColor: initialDarkData?.editIconColor || "#ffffff",
      viewButtonColor: initialDarkData?.viewButtonColor || "#858d74",
      viewIconColor: initialDarkData?.viewIconColor || "#ffffff",
      deleteButtonColor: initialDarkData?.deleteButtonColor || "#765b58",
      deleteIconColor: initialDarkData?.deleteIconColor || "#ffffff",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = initialDarkData
          ? { ...values, id: initialDarkData.id }
          : { ...values };

        const response = await darkthemeData(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as IAuth;
          if (tokenData) {
            localStorage.setItem("darkThemeColor", JSON.stringify(tokenData));
            window.location.reload();
          }
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.primary !== "";
  }, [formik]);

  const formValidDark = useMemo(() => {
    return formikDark.values.primary !== "";
  }, [formikDark]);

  const openColorPicker = (field: keyof ILightTheme) => {
    setTempColor(formik.values[field]);
    setSelectedField(field);
  };

  const openColorPickerDark = (field: keyof IDarkTheme) => {
    setTempColorDark(formikDark.values[field]);
    setSelectedDarkField(field);
  };

  const applyColor = () => {
    if (selectedField) {
      formik.setFieldValue(selectedField, tempColor);
    }
    setSelectedField("");
  };

  const applyColorDark = () => {
    if (selectedDarkField) {
      formikDark.setFieldValue(selectedDarkField, tempColorDark);
    }
    setSelectedDarkField("");
  };

  const renderContent = () => {
    switch (activeSection) {
      case Section.Light:
        return (
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {[
                    { label: "Primary", field: "primary" },
                    { label: "Secondary", field: "secondary" },
                    {
                      label: "Text Primary",
                      field: "textPrimary",
                    },
                    {
                      label: "Text Secondary",
                      field: "textSecondary",
                    },
                    { label: "Heading", field: "heading" },
                    { label: "Divider", field: "divider" },
                    { label: "Top Bar", field: "topBar" },
                    { label: "Top Bar Heading", field: "topBarHeading" },

                    { label: "Drawer Header", field: "drawerHeader" },
                    { label: "Drawer", field: "drawer" },
                    { label: "Drawer Heading Text", field: "drawerHeadingText" },
                    { label: "Drawer Menu Text", field: "drawerMenuText" },
                    { label: "Background", field: "background" },
                    {
                      label: "Second Background (Search)",
                      field: "secondBackground",
                    },
                    { label: "Card Background", field: "cardBackground" },
                    { label: "Edit Button Color", field: "editButtonColor" },
                    { label: "Edit Icon Color", field: "editIconColor" },
                    { label: "View Button Color", field: "viewButtonColor" },
                    { label: "View Icon Color", field: "viewIconColor" },
                    {
                      label: "Delete Button Color",
                      field: "deleteButtonColor",
                    },
                    { label: "Delete Icon Color", field: "deleteIconColor" },
                  ].map(({ label, field }) => (
                    <Grid item lg={3} md={6} sm={6} xs={12} key={field}>
                      <Card className={classes.card}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          p={2}
                        >
                          <Box>
                            <Typography variant="subtitle1">{label}</Typography>
                            <Box display="flex" alignItems="center" mt={1}>
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor:
                                    formik.values[field as keyof ILightTheme],
                                  border: "1px solid #d3d3d3",
                                  borderRadius: "8px",
                                  marginRight: 2,
                                }}
                              />
                              {/* <Typography variant="body1">
                          {formik.values[field as keyof ILightTheme]}
                        </Typography> */}
                              <TextField
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: 14,
                                    border: "none",
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                value={
                                  formik.values[field as keyof ILightTheme]
                                }
                                onChange={(e) =>
                                  formik.setFieldValue(field, e.target.value)
                                }
                              />
                            </Box>
                          </Box>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              openColorPicker(field as keyof ILightTheme)
                            }
                          >
                            <ColorLensIcon sx={{ fontSize: 40 }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Border Radius (update, Add, search, cancel)
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Border Radius"
                              size="small"
                              {...formik.getFieldProps("borderRadius")}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Icon Button Border Radius (List - edit,delete,view)
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Button Border Radius"
                              size="small"
                              {...formik.getFieldProps(
                                "IconButtonBorderRadius"
                              )}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Card Border Radius
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Card Border Radius"
                              size="small"
                              {...formik.getFieldProps("cardBorderRadius")}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<SaveAltIcon />}
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {selectedField && (
              <Dialog
                open={!!selectedField}
                onClose={() => setSelectedField("")}
              >
                <Grid className={classes.Dialog}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Set Color
                      </Typography>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <HexColorPicker
                        color={tempColor}
                        onChange={setTempColor}
                        style={{
                          width: "250px",
                          height: "250px",
                        }}
                      />
                    </Box>
                  </DialogContent>

                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => setSelectedField("")}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<ColorLensIcon />}
                          className={classes.addButton}
                          onClick={applyColor}
                        >
                          Apply Color
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Grid>
              </Dialog>
            )}
          </Grid>
        );
      case Section.Dark:
        return (
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <form onSubmit={formikDark.handleSubmit}>
                <Grid container spacing={3}>
                  {[
                    { label: "Primary", field: "primary" },
                    { label: "Secondary", field: "secondary" },
                    {
                      label: "Text Primary",
                      field: "textPrimary",
                    },
                    {
                      label: "Text Secondary",
                      field: "textSecondary",
                    },
                    { label: "Heading", field: "heading" },
                    { label: "Divider", field: "divider" },
                    { label: "Top Bar", field: "topBar" },
                    { label: "Top Bar Heading", field: "topBarHeading" },
                    { label: "Drawer Header", field: "drawerHeader" },
                    { label: "Drawer", field: "drawer" },
                    { label: "Drawer Heading Text", field: "drawerHeadingText" },
                    { label: "Drawer Menu Text", field: "drawerMenuText" },
                    { label: "Background", field: "background" },
                    {
                      label: "Second Background (Search)",
                      field: "secondBackground",
                    },
                    { label: "Card Background", field: "cardBackground" },
                    { label: "Edit Button Color", field: "editButtonColor" },
                    { label: "Edit Icon Color", field: "editIconColor" },
                    { label: "View Button Color", field: "viewButtonColor" },
                    { label: "View Icon Color", field: "viewIconColor" },
                    {
                      label: "Delete Button Color",
                      field: "deleteButtonColor",
                    },
                    { label: "Delete Icon Color", field: "deleteIconColor" },
                  ].map(({ label, field }) => (
                    <Grid item lg={3} md={6} sm={6} xs={12} key={field}>
                      <Card className={classes.card}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          p={2}
                        >
                          <Box>
                            <Typography variant="subtitle1">{label}</Typography>
                            <Box display="flex" alignItems="center" mt={1}>
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor:
                                    formikDark.values[
                                      field as keyof IDarkTheme
                                    ],
                                  border: "1px solid #d3d3d3",
                                  borderRadius: "8px",
                                  marginRight: 2,
                                }}
                              />
                              {/* <Typography variant="body1">
                          {formik.values[field as keyof ILightTheme]}
                        </Typography> */}
                              <TextField
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: 14,
                                    border: "none",
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                value={
                                  formikDark.values[field as keyof IDarkTheme]
                                }
                                onChange={(e) =>
                                  formikDark.setFieldValue(
                                    field,
                                    e.target.value
                                  )
                                }
                              />
                            </Box>
                          </Box>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              openColorPickerDark(field as keyof IDarkTheme)
                            }
                          >
                            <ColorLensIcon sx={{ fontSize: 40 }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<SaveAltIcon />}
                            className={classes.addButton}
                            onClick={() => formikDark.handleSubmit()}
                            disabled={!formValidDark}
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {selectedDarkField && (
              <Dialog
                open={!!selectedDarkField}
                onClose={() => setSelectedDarkField("")}
              >
                <Grid className={classes.Dialog}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Set Color
                      </Typography>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <HexColorPicker
                        color={tempColorDark}
                        onChange={setTempColorDark}
                        style={{
                          width: "250px",
                          height: "250px",
                        }}
                      />
                    </Box>
                  </DialogContent>

                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => setSelectedDarkField("")}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<ColorLensIcon />}
                          className={classes.addButton}
                          onClick={applyColorDark}
                        >
                          Apply Color
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Grid>
              </Dialog>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Card className={classes.stepperCard}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={4}
              md={4}
              sm={6}
              xs={6}
              pt={2}
              alignContent={"center"}
            >
              <Typography className={classes.cardTitle}>
                Set Theme Color
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{ display: "flex" }}
              >
                <Grid item>
                  <Button
                    variant="contained"
                    className={classes.addButton}
                    onClick={() => {
                      handleContainerChange(
                        mode === "light" ? Section.Dark : Section.Light
                      );
                      toggleTheme();
                    }}
                  >
                    {mode === "light" ? "Dark" : "light"}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        {renderContent()}
      </Grid>
    </Grid>
  );
};

export default Index;
